import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ShieldCheck } from "lucide-react";
const links = [
  { to: "/", label: "Home" },
  { to: "/jobs", label: "Browse Jobs" },
  { to: "/for-employers", label: "For Employers" },
  { to: "/career-pathway", label: "Career Pathway" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // All pages now have light backgrounds, so nav is always "scrolled" style (glass + dark text).
  const scrolled = true;

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 inset-x-0 z-50 transition-all duration-500 glass-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <div className={`h-10 w-10 rounded-md flex items-center justify-center ring-1 transition-all duration-300 ${
            scrolled
              ? "bg-navy ring-navy/10"
              : "bg-[#10B981] ring-[#10B981]/30 shadow-[0_4px_18px_rgba(16,185,129,0.45)]"
          }`}>
            <ShieldCheck className={`h-4 w-4 transition-colors ${scrolled ? "text-[#D4AF37]" : "text-white"}`} strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div
              className={`font-serif text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${
                scrolled ? "text-navy" : "text-white"
              }`}
              style={!scrolled ? {
                textShadow: "0 1px 0 rgba(255,255,255,0.35), 0 2px 0 rgba(0,0,0,0.25), 0 4px 14px rgba(0,0,0,0.55)",
                letterSpacing: "-0.01em",
              } : { textShadow: "0 1px 0 rgba(255,255,255,0.6)" }}
            >
              Direct Hire Team
            </div>
            <div
              className={`eyebrow text-[10px] font-semibold transition-colors duration-300 ${
                scrolled ? "text-[#10B981]" : "text-[#D4AF37]"
              }`}
              style={!scrolled ? { textShadow: "0 1px 4px rgba(0,0,0,0.5)" } : null}
            >
              Remote jobs · All 50 states
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-semibold tracking-wide transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:bg-[#10B981] after:transition-all after:duration-300 ${
                  scrolled
                    ? "text-navy hover:text-[#10B981]"
                    : "text-white hover:text-[#10B981]"
                } ${isActive ? "after:w-full" : "after:w-0 hover:after:w-full"}`
              }
              style={{ fontFamily: "'Outfit', sans-serif" }}
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/jobs" data-testid="nav-cta-browse" className={`text-sm font-semibold tracking-wide transition-colors px-4 py-2.5 rounded-full ring-1 ${
            scrolled
              ? "text-[#10B981] ring-[#10B981]/30 hover:bg-[#10B981]/10"
              : "text-white ring-white/30 hover:bg-white/10"
          }`}>
            Find a Job
          </Link>
          <Link to="/for-employers" data-testid="nav-cta-hire" className="btn-emerald text-sm px-5 py-2.5">
            Hire Talent
          </Link>
        </div>

        <button
          onClick={() => setOpen((s) => !s)}
          className={`md:hidden p-2 rounded-md transition-colors ${scrolled ? "hover:bg-navy/5" : "hover:bg-white/10"}`}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open
            ? <X className={`h-5 w-5 ${scrolled ? "text-navy" : "text-white"}`} />
            : <Menu className={`h-5 w-5 ${scrolled ? "text-navy" : "text-white"}`} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass-nav border-t border-navy/5" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 space-y-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block py-3 text-base ${isActive ? "text-navy font-semibold" : "text-navy/70"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/for-employers" className="btn-emerald w-full mt-2" data-testid="nav-mobile-cta-hire">
              Hire Talent
            </Link>
            <Link to="/jobs" className="btn-ghost w-full mt-2 !justify-center" data-testid="nav-mobile-cta-browse">
              Find a Job
            </Link>
            <Link to="/enroll" className="block text-center text-xs mt-2 text-navy/55 hover:text-[#10B981]" data-testid="nav-mobile-cta-prc">
              Free PRC certificate →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
